import React from 'react';

/**
 * Card de um valor em destaque, com a composição dele por baixo (at-62, D1).
 *
 * ## Por que virou componente
 *
 * É o card "Mensalidade" que a at-60 desenhou em `/produto` — título à esquerda,
 * valor em destaque à direita, composição em letra pequena embaixo, e um estado
 * de "não deu para calcular". A tela de cotação mostra **a mesma coisa** com
 * outro nome ("Boleto médio"), e antes desta branch a fazia com três linhas
 * soltas de `.form-item-titulo`. Duas telas com a mesma regra de apresentação é
 * o gatilho da convenção da at-24.
 *
 * ⚠️ **O componente é burro**: não soma, não formata e não sabe o que é rateio.
 * Recebe o valor **já formatado** (`numeroParaMoeda`) e o detalhe pronto — quem
 * conhece a composição é a tela. Mesma filosofia do `Chip` e do `DataList`.
 *
 * 🔴 **`indisponivel` NÃO é um estado de erro** e por isso é âmbar, não vermelho:
 * em `/produto` ele aparece quando o rateio não pôde ser calculado, e a tela
 * continua funcionando (a seleção de produtos e o salvamento não dependem dele).
 * Vermelho aqui pareceria falha da tela — a distinção é a mesma que o
 * `13-ui-kit.md` faz entre `ErrorState` e `Chip pendente`.
 */

interface PropsResumoValor {
  titulo: string;
  /** Já formatado — ex.: `numeroParaMoeda(mensalidade)`. */
  valor?: string;
  /** A composição do valor, em letra pequena. Ex.: "cota X + rateio Y". */
  detalhe?: React.ReactNode;
  /**
   * Quando presente, **substitui** título e valor: é o caso de não ter sido
   * possível calcular. Texto em linguagem de usuário, dizendo o que faltou.
   */
  indisponivel?: string;
  className?: string;
}

const ResumoValor: React.FC<PropsResumoValor> = ({
  titulo,
  valor,
  detalhe,
  indisponivel,
  className = '',
}) => (
  <div
    className={[
      'tw-rounded-xl tw-border tw-border-superficie-borda tw-bg-superficie tw-p-4',
      className,
    ]
      .filter(Boolean)
      .join(' ')}
  >
    {indisponivel ? (
      <p className="tw-m-0 tw-text-sm tw-text-amber-deep">{indisponivel}</p>
    ) : (
      <>
        <div className="tw-flex tw-items-baseline tw-justify-between tw-gap-3">
          <span className="tw-text-sm tw-font-semibold tw-text-texto-titulo">{titulo}</span>
          <strong className="tw-font-marca tw-text-xl tw-font-bold tw-text-brand">{valor}</strong>
        </div>
        {detalhe && <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-texto-suave">{detalhe}</p>}
      </>
    )}
  </div>
);

export default ResumoValor;
